import { useEffect } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router'

import { Shell } from '@/app/Shell'
import { resetSession } from '@/app/lib/sessionReset'
import NotFound from '@/app/pages/NotFound'
import QuantShell from '@/app/pages/QuantShell'
import QuantManual from '@/app/pages/QuantManual'
import QuantStrategies from '@/app/pages/QuantStrategies'
import Unlock from '@/app/pages/Unlock'
import { clearToken, UNLOCK_EVENT } from '@/app/lib/api'

/** Any 401 sends the visitor to the unlock screen (and forgets a token the server just refused). */
function UnlockListener() {
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const onUnlockRequired = () => {
      clearToken()
      resetSession()
      if (location.pathname === '/unlock') return
      void navigate('/unlock', { replace: true, state: { from: location.pathname + location.search } })
    }
    window.addEventListener(UNLOCK_EVENT, onUnlockRequired)
    return () => window.removeEventListener(UNLOCK_EVENT, onUnlockRequired)
  }, [navigate, location.pathname, location.search])

  return null
}

/** `/strategies/:slug` lands on the strategy's Config tab. */
function StrategyDefaultTabRedirect() {
  const { slug } = useParams<{ slug: string }>()
  return <Navigate to={`/strategies/${slug}/config`} replace />
}

/** Every route of the app (spec 004 D2). Old QMC URLs redirect to the nearest new screen. */
export function AppRoutes() {
  return (
    <>
      <UnlockListener />
      <Routes>
        <Route path="/unlock" element={<Unlock />} />
        <Route element={<Shell />}>
          <Route element={<QuantShell />}>
            <Route index element={<Navigate to="/strategies" replace />} />
            <Route path="/strategies" element={<QuantStrategies />} />
            <Route path="/strategies/:slug" element={<StrategyDefaultTabRedirect />} />
            <Route path="/strategies/:slug/config" element={<QuantStrategies />} />
            <Route path="/strategies/:slug/runs" element={<QuantStrategies />} />
            <Route path="/strategies/:slug/runs/:runId" element={<QuantStrategies />} />
            <Route path="/strategies/:slug/live" element={<QuantStrategies />} />
            <Route path="/strategies/:slug/live/:sessionId" element={<QuantStrategies />} />
            <Route path="/manual" element={<Navigate to="/manual/NQ?tf=5m" replace />} />
            <Route path="/manual/:symbol" element={<QuantManual />} />
          </Route>
          {/* Pre-0.4.0 URLs. */}
          <Route path="/backtests/*" element={<Navigate to="/strategies" replace />} />
          <Route path="/live/*" element={<Navigate to="/strategies" replace />} />
          <Route path="/sandbox" element={<Navigate to="/manual" replace />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </>
  )
}
